import { useQuery } from "@tanstack/react-query";
import { LeadActivity } from "views/leads-id/types";
import { inboxApi } from "views/inbox/api/api";
import { CallRecordingPlayer } from "shared/components/CallRecordingPlayer";
import { Show } from "shared/components/helpers/Show";
import { aiHandoffReasonText } from "shared/utils/ai-handoff";

const formatDuration = (seconds: number | null | undefined) => {
  if (!seconds || seconds <= 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins === 0) return `${secs} sec`;
  return `${mins} min ${secs.toString().padStart(2, "0")} sec`;
};

export const AICallActivityCard = ({
  activity
}: {
  activity: LeadActivity;
}) => {
  const callTranscriptId = activity.metadata.call_transcript_id;
  const durationSeconds = activity.metadata.duration_seconds;
  const { metadata } = activity;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["CALL_TRANSCRIPT", callTranscriptId],
    queryFn: () => inboxApi.getCallTranscript(callTranscriptId!),
    enabled: !!callTranscriptId
  });

  const recordingUrl = data?.recording_url;
  const effectiveDuration = data?.recording_duration_seconds ?? durationSeconds;

  const subline = [
    aiHandoffReasonText(metadata.team_first_route, metadata.handed_off_by_name),
    formatDuration(effectiveDuration),
    callTranscriptId ? "recording and transcript" : null
  ]
    .filter(Boolean)
    .join(" · ");

  const getRecordingFallback = () => {
    if (isLoading) return "Loading recording...";
    if (isError) return "Couldn't load recording";
    return "Recording unavailable";
  };

  return (
    <div className="max-w-md space-y-1">
      <Show if={Boolean(subline)}>
        <p className="text-sm text-content-400">{subline}</p>
      </Show>
      {recordingUrl ? (
        <CallRecordingPlayer
          recordingUrl={recordingUrl}
          durationSeconds={effectiveDuration}
        />
      ) : (
        <p className="text-xs text-content-200">{getRecordingFallback()}</p>
      )}
    </div>
  );
};
